import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronRight, LayoutDashboard, Plug, ShieldCheck, LogOut, TrendingUp, CreditCard, Sparkles, Users, LifeBuoy } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/calia", label: "Ask Calia", icon: Sparkles, testid: "nav-calia" },
  { to: "/insights", label: "CRM Insights", icon: TrendingUp, testid: "nav-insights" },
  { to: "/connect", label: "Connect CRM", icon: Plug, testid: "nav-connect" },
  { to: "/billing", label: "Plans & Billing", icon: CreditCard, testid: "nav-billing" },
  { to: "/team", label: "Team & Access", icon: Users, testid: "nav-team" },
  { to: "/support", label: "Support", icon: LifeBuoy, testid: "nav-support" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const nav = [...NAV];
  if (user?.role === "admin") nav.push({ to: "/admin/command-center", label: "Admin", icon: ShieldCheck, testid: "nav-admin" });
  const doLogout = async () => { await logout(); navigate("/login"); };

  return <div className="min-h-screen overflow-x-hidden bg-[#f4f7fb] text-slate-950">
    <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 flex-col overflow-hidden bg-[#081426] text-white md:flex">
      <div className="absolute inset-0 opacity-40" style={{background:"radial-gradient(circle at 15% 0%, #2563eb 0, transparent 32%), radial-gradient(circle at 80% 90%, #0f766e 0, transparent 28%)"}} />
      <div className="relative flex h-20 items-center border-b border-slate-200 bg-white px-5">
        <img src="/cleaniq-logo-compact.webp" alt="CleanIQ — Clean Data. Smarter CRM." className="h-auto w-48 max-w-full object-contain" />
      </div>
      <nav className="relative flex-1 space-y-1.5 p-4">
        <div className="mb-3 px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Workspace</div>
        {nav.map((n) => {
          const active = location.pathname === n.to;
          const Icon = n.icon;
          return <Link key={n.to} to={n.to} data-testid={n.testid}
            className={"group flex min-h-touch items-center gap-3 rounded-xl px-3.5 py-3 text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 " +
              (active ? "bg-white text-slate-950 shadow-xl shadow-black/10" : "text-slate-300 hover:bg-white/10 hover:text-white")}>
            <Icon className={"h-4 w-4 " + (active ? "text-blue-600" : "text-slate-400 group-hover:text-cyan-300")} />
            {n.label}
          </Link>;
        })}
      </nav>
      <div className="relative m-4 rounded-2xl border border-white/10 bg-white/5 p-4 backdrop-blur">
        <div className="mb-3 flex items-center gap-3">
          <img src="/cleaniq-data-flow.jpg" alt="" aria-hidden="true" className="h-14 w-14 shrink-0 rounded-xl object-cover ring-1 ring-white/15" />
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="truncate text-xs text-slate-400">{user?.email}</div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-xs text-amber-200"><Sparkles className="h-3.5 w-3.5" /> Clean data. Clear decisions.</div>
        <button data-testid="logout-button" onClick={doLogout}
          className="mt-4 flex min-h-touch w-full items-center gap-2 rounded-customer border border-white/10 px-3 py-2 text-xs text-slate-300 transition hover:bg-white/10 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </aside>

    <div className="min-w-0 md:pl-64">
      <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-slate-200/80 bg-white/90 px-4 backdrop-blur-xl sm:px-6 md:h-20 lg:px-8">
        <div className="flex min-w-0 items-center gap-3">
          <img src="/favicon-32.png" alt="CleanIQ" className="h-8 w-8 shrink-0 object-contain md:hidden" />
          <div className="min-w-0"><div className="truncate text-sm font-semibold text-slate-900">CRM Data Quality Workspace</div><div className="hidden text-xs text-slate-500 sm:block">Turn messy records into trusted customer data</div></div>
        </div>
        <button data-testid="mobile-logout-button" aria-label="Sign out" onClick={doLogout}
          className="flex min-h-touch min-w-touch items-center gap-2 rounded-customer border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 transition hover:bg-slate-100 hover:text-slate-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 md:hidden">
          <LogOut className="h-4 w-4" /><span className="hidden sm:inline">Sign out</span>
        </button>
      </header>
      <div className="relative border-b border-slate-200 bg-white px-3 py-2 md:hidden">
        <nav className="flex gap-1 overflow-x-auto pb-1 pr-10" aria-label="Customer workspace navigation">
          {nav.map((n) => { const Icon=n.icon; const active=location.pathname===n.to; return <Link key={n.to} to={n.to}
            className={"flex min-h-touch shrink-0 items-center gap-2 rounded-customer px-3 py-2 text-xs font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 "+(active?"bg-slate-950 text-white":"text-slate-600 hover:bg-slate-100")}>
            <Icon className="h-3.5 w-3.5" />{n.label}</Link>; })}
        </nav>
        <div
          className="pointer-events-none absolute inset-y-2 right-0 flex w-12 items-center justify-end bg-gradient-to-r from-transparent via-white/90 to-white pr-2 text-slate-400"
          aria-hidden="true"
          data-testid="mobile-nav-overflow-cue"
        >
          <ChevronRight className="h-4 w-4" />
        </div>
      </div>
      <main className="min-w-0 p-4 sm:p-6 lg:p-8 xl:p-10">
        <div className="mx-auto w-full max-w-[1500px]">{children}</div>
      </main>
    </div>
  </div>;
}
